import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Header, Icon, Input, Message, Modal, Table } from 'react-frontier';
import { useBreadcrumbs, useUser } from '../Hooks';
import { SurveyResponseItem, SurveyQuestionType, SetLoading } from '../SiteClasses';
import { bindClick, formatUnix } from '../SiteUtil';
import UserAccess from '../UserAccess';
import Toast from '../Toast';
import API, { PAGE_URL } from '../API';

enum Modals{
	LINK = 1,
	RESPONSE = 2,
}

var PromoterSurveys = ()=>{
	var params = useParams<{ promoter_id?: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var promoter_id = params.promoter_id ? parseInt(params.promoter_id) : null;
	var canEdit = hasAccess(UserAccess.PROMOTERS.EDIT_PROMOTERS);

	var [responses, setResponses] = useState<SurveyResponseItem[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [link, setLink] = useState<string>(null);
	var [linkError, setLinkError] = useState<string>(null);
	var [response, setResponse] = useState<SurveyResponseItem>(null);
	var [responseError, setResponseError] = useState<string>(null);

	useEffect(()=>{ setTitle('Encuestas'); }, []);
	useEffect(()=>{ get(); }, [promoter_id]);

	var get = ()=>{
		if(!promoter_id) return;
		setLoadError(null);
		API.getSurveyHistory(promoter_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setResponses(res.data || []);
		}).catch(()=>{
			setLoadError('Hubo un error cargando el historial de encuestas. (PSV-1)');
		});
	};

	var generateLink = (setLoading: SetLoading)=>{
		setLink(null);
		setLinkError(null);
		setShownModal(Modals.LINK);
		setLoading(true);
		API.createSurveyLink(promoter_id).then(res=>{
			if(res.error) return setLinkError(res.message);
			var base = PAGE_URL || window.location.origin;
			setLink(`${base.replace(/\/$/, '')}/survey/${res.data.token}`);
		}).catch(()=>{
			setLinkError('Hubo un error generando el link de la encuesta. (PSV-2)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var copyLink = ()=>{
		if(!link) return;
		navigator.clipboard.writeText(link).then(()=>{
			Toast.success('Link copiado');
		}).catch(()=>{
			Toast.error('No se pudo copiar el link');
		});
	};

	var openResponse = (r: SurveyResponseItem)=>{
		setResponse(null);
		setResponseError(null);
		setShownModal(Modals.RESPONSE);
		API.getSurveyResponse(r.response_id).then(res=>{
			if(res.error) return setResponseError(res.message);
			setResponse(res.data);
		}).catch(()=>{
			setResponseError('Hubo un error cargando las respuestas. (PSV-3)');
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;

	return <div>
		<Table striped style={{ maxWidth: 500, margin: 'auto', marginTop: 20 }}>
			<Table.Head
				title='Encuestas'
				titleRight={canEdit ? (
					<Button text='Nueva encuesta' iconName='circle-plus' color='black' size='small' onClick={generateLink} />
				) : undefined}
			>
				<Table.Row>
					<Table.Cell header>ID</Table.Cell>
					<Table.Cell header centered>Respuestas</Table.Cell>
					<Table.Cell header>Fecha</Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{!responses ? (
					<Table.Row>
						<Table.Cell colSpan={3}>
							<Header loading text='Cargando historial' style={{ paddingTop: 0 }} />
						</Table.Cell>
					</Table.Row>
				) : responses.length === 0 ? (
					<Table.Row>
						<Table.Cell colSpan={3} empty>Este promotor no ha contestado ninguna encuesta</Table.Cell>
					</Table.Row>
				) : responses.map(r=>(
					<Table.Row key={`SVRSP-${r.response_id}`} selectable onClick={()=>openResponse(r)}>
						<Table.Cell>{r.response_id}</Table.Cell>
						<Table.Cell centered>{r.answer_count}</Table.Cell>
						<Table.Cell collapsing>{formatUnix(r.date_created, 'dd/MM/yyyy')}</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>
		<Modal open={shownModal===Modals.LINK} onClose={bindClick(setShownModal, null)} size='small'>
			<Modal.Header text='Link de la encuesta' />
			<Modal.Content>
				{!!linkError && <Message type='error' list={[linkError]} />}
				{!link && !linkError ? (
					<Header loading text='Generando link' />
				) : !!link && (<>
					<Header
						text='Comparte este link con el promotor'
						subtext='El link identifica al promotor, solo se puede contestar una vez y vence en 24 horas.'
						size='small'
					/>
					<Input
						fluid
						readonly
						value={link}
						style={{ marginTop: 10 }}
						buttons={<Button iconName='copy' onClick={copyLink} />}
					/>
				</>)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.RESPONSE} onClose={bindClick(setShownModal, null)} size='small'>
			<Modal.Header text={response ? `Encuesta #${response.response_id}` : 'Encuesta'} />
			<Modal.Content style={{ padding: 0, maxHeight: 500, overflowY: 'auto' }}>
				{!!responseError && <Message type='error' list={[responseError]} />}
				{!response && !responseError ? (
					<Header loading text='Cargando respuestas' />
				) : !!response && (
					<Table striped fitted details>
						<Table.Head>
							<Table.Row>
								<Table.Cell header>Pregunta</Table.Cell>
								<Table.Cell header>Respuesta</Table.Cell>
							</Table.Row>
						</Table.Head>
						<Table.Body>
							{(!response.answers || response.answers.length === 0) ? (
								<Table.Row>
									<Table.Cell colSpan={2} empty>Sin respuestas</Table.Cell>
								</Table.Row>
							) : response.answers.map(a=>(
								<Table.Row key={`SVANS-${a.answer_id}`}>
									<Table.Cell>{a.question}</Table.Cell>
									<Table.Cell centered>{a.type_id===SurveyQuestionType.YESNO ? (a.answer==='1' ? 'Sí' : 'No') : a.answer}</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default PromoterSurveys;
